import { SelectTypes } from '@genial/components';
import { Service } from '@genial/services';
import {
  BeneficiarioDTO,
  PortabilidadeDTO,
  ResponsavelDTO,
  SolicitarAplicacaoPrevidencia,
} from '@genial/services/dist/openapi';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRecoilState } from 'recoil';
import { z } from 'zod';

import { useRecoilQuery } from '../../../../../../../hooks/useRecoilQuery';
import { groupedCustomerDetailQuery } from '../../../../../../../store/customerDetail/selector';
import { privatePensionOrderState } from '../../../../../../../store/product/atom';
import { defaultDebitDueDates } from '../../../../../../../store/product/constants';
import { ReturnTypeOf_PrevidenciaObterCertificadosAptosPortabilidade } from '../../../../../../../store/product/external/types';
import { normalizeBankCode } from '../../../../../../../store/product/normalizer';
import {
  privatePensionClientBankAccountsQuery,
  privatePensionInsuranceQuery,
  privatePensionInsuranceCompanyParameters,
  privatePensionRelatedPartyQuery,
} from '../../../../../../../store/product/selectors';
import {
  PrivatePensionClientBankAccountEntityNormalized,
  PrivatePensionEntityNormalized,
} from '../../../../../../../store/product/types';

import {
  PrivatePensionAccountHolder,
  PrivatePensionBeneficiary,
  PrivatePensionKind,
  PrivatePensionPaymentMethod,
  PrivatePensionPortabilityInfo,
  PrivatePensionPortabilityRentabilityType,
  PrivatePensionTaxSystem,
} from './types';

export const usePrivatePensionAssetController = (
  offer: PrivatePensionEntityNormalized,
  accountId: number,
  accountNumber: number
) => {
  const { data: relatedPartyData, loading: relatedPartyLoading } = useRecoilQuery(
    privatePensionRelatedPartyQuery({ insuranceCompanyId: offer.insuranceCompanyId })
  );

  const {
    data: customerData,
    loading: customerLoading,
    error: customerError,
    refresher: customerRefresher,
  } = useRecoilQuery(groupedCustomerDetailQuery({ accountNumber }));

  const { data: insuranceData, loading: insuranceLoading } = useRecoilQuery(
    privatePensionInsuranceQuery
  );

  const { data: bankAccountsData, loading: bankAccountsLoading } = useRecoilQuery(
    privatePensionClientBankAccountsQuery({ accountId })
  );

  const { data: insuranceCompanyParametersData, loading: insuranceCompanyParametersLoading } =
    useRecoilQuery(
      privatePensionInsuranceCompanyParameters({ insuranceCompanyId: offer.insuranceCompanyId })
    );

  const [orderState, setOrderState] = useRecoilState(privatePensionOrderState);
  const [relatedPartyOptions, setRelatedPartyOptions] = useState<SelectTypes.Option[]>([]);
  const [fundOptions, setFundOptions] = useState<SelectTypes.Option[]>([]);
  const [insuranceOptions, setInsuranceOptions] = useState<SelectTypes.Option[]>([]);
  const [bankOptions, setBankOptions] = useState<SelectTypes.Option[]>([]);
  const [legacySendingChannel, setLegacySendingChannel] = useState('');
  const [bankAccounts, setBankAccounts] = useState<
    PrivatePensionClientBankAccountEntityNormalized[]
  >([]);

  const hasLegacySendingChannel = useMemo(() => {
    return legacySendingChannel !== '';
  }, [legacySendingChannel]);

  const legacySendingChannelIsOther = useMemo(() => {
    return legacySendingChannel === 'Outros';
  }, [legacySendingChannel]);

  const legacySendingChannelsOptions = useMemo(() => {
    return [
      {
        value: 'Telefone',
        label: 'Telefone',
      },
      {
        value: 'E-mail',
        label: 'E-mail',
      },
      {
        value: 'Outros',
        label: 'Outros',
        isOther: true,
      },
    ];
  }, []);

  const taxSystemOptions = useMemo(
    () => [
      {
        value: PrivatePensionTaxSystem.Progressive.toString(),
        label: 'Progressivo',
      },
      {
        value: PrivatePensionTaxSystem.Regressive.toString(),
        label: 'Regressivo',
      },
    ],
    []
  );

  const paymentMethodOptions = useMemo(
    () => [
      {
        value: PrivatePensionPaymentMethod.Debit.toString(),
        label: 'Débito',
      },
      {
        value: PrivatePensionPaymentMethod.BankSlip.toString(),
        label: 'Boleto',
      },
    ],
    []
  );

  const bankAccountOptions = useMemo(() => {
    const hasManualBankAccount = orderState.bankAccounts.some((account) => account.manuallyAdded);

    const bankAccountsToUse = hasManualBankAccount
      ? orderState.bankAccounts.filter((account) => !account.manuallyAdded)
      : bankAccountsData?.data;

    return (
      bankAccountsToUse?.map((bankAccount) => ({
        value: bankAccount.bankAccountId.toString(),
        label: `${bankAccount.bankName} - Conta: ${bankAccount.accountNumber} / Agência: ${bankAccount.agencyNumber}`,
      })) || []
    );
  }, [bankAccountsData, orderState]);

  const resolveBankCodeByAccountId = useCallback(
    (bankAccountId?: number) => {
      if (bankAccountId === undefined) {
        return '';
      }

      const bankAccount =
        bankAccountsData?.data?.find((account) => account.bankAccountId === bankAccountId) ??
        orderState.bankAccounts.find((account) => account.bankAccountId === bankAccountId);

      return normalizeBankCode(bankAccount?.bankCode);
    },
    [bankAccountsData?.data, orderState.bankAccounts]
  );

  const dueDateOptions = useMemo(() => {
    if (orderState.paymentMethod === PrivatePensionPaymentMethod.BankSlip) {
      return insuranceCompanyParametersData?.boletoDueDates ?? [];
    }

    if (orderState.bankAccountId === undefined) {
      return [];
    }

    const bankCode = resolveBankCodeByAccountId(orderState.bankAccountId);
    return (
      insuranceCompanyParametersData?.debitDueDatesByBankCode[bankCode] ?? defaultDebitDueDates
    );
  }, [
    insuranceCompanyParametersData,
    orderState.bankAccountId,
    orderState.paymentMethod,
    resolveBankCodeByAccountId,
  ]);

  const isDueDateEnabled = useMemo(() => {
    if (orderState.paymentMethod === PrivatePensionPaymentMethod.BankSlip) {
      return true;
    }

    return orderState.bankAccountId !== undefined;
  }, [orderState.bankAccountId, orderState.paymentMethod]);

  const hasPortability = orderState.portabilities.length > 0;
  const hasBeneficiary = orderState.beneficiaries.length > 0;
  const portabilityAmount = orderState.portabilities.reduce(
    (acc, curPortability) => acc + curPortability.value,
    0
  );

  const currentParticipationPercentage = orderState.beneficiaries.reduce(
    (acc, curBeneficiary) => acc + curBeneficiary.participationPercentage,
    0
  );

  let maxParticipationPercentage = 100 - currentParticipationPercentage;

  if (maxParticipationPercentage < 0) {
    maxParticipationPercentage = 0;
  }

  const accountHolderSchema = useMemo(
    () =>
      z
        .object({
          paymentMethod: z.nativeEnum(PrivatePensionPaymentMethod, {
            description: 'Método de pagamento',
            required_error: 'Método de pagamento é obrigatório',
          }),
          dueDate: z.number({
            description: 'Data de vencimento',
            required_error: 'Data de vencimento é obrigatória',
          }),
          bankAccountId: z.number().optional(),
          singleContribution: z
            .string({
              required_error: 'Contribuição única é obrigatória',
            })
            .refine((value) => Utils.Unmask.Money(value) >= offer.minimumApplication, {
              message: `Contribuição única deve ser maior que ${Utils.Mask.Money(
                offer.minimumApplication
              )}`,
            })
            .transform((value) => Utils.Unmask.Money(value)),
          monthlyContribution: z
            .string({
              required_error: 'Contribuição mensal é obrigatória',
            })
            .refine((value) => Utils.Unmask.Money(value) >= offer.monthlyMinimumApplication, {
              message: `Contribuição mensal deve ser maior que ${Utils.Mask.Money(
                offer.monthlyMinimumApplication
              )}`,
            })
            .transform((value) => Utils.Unmask.Money(value)),
          pensionAge: z
            .number({
              description: 'Idade de aposentadoria desejada',
              required_error: 'Idade de aposentadoria desejada é obrigatória',
            })
            .refine((value) => value > 0, {
              message: 'Idade de aposentadoria tem que ser maior que zero',
            }),
          taxSystem: z.nativeEnum(PrivatePensionTaxSystem, {
            description: 'Tipo de regime tributário',
            required_error: 'Tipo de regime tributário é obrigatório',
          }),
          beneficiariesPercentage: z
            .number({
              description: 'Porcentagem de participação',
              required_error: 'Porcentagem de participação é obrigatória',
            })
            .refine((value) => (hasBeneficiary ? value === 100 : true), {
              message: 'A participação total deve ser de 100%',
            }),
        })
        .superRefine((data, ctx) => {
          if (
            data.paymentMethod !== PrivatePensionPaymentMethod.BankSlip &&
            data.bankAccountId === undefined
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Conta bancária é obrigatória',
              path: ['bankAccountId'],
            });
          }
        }),
    [hasBeneficiary, offer.minimumApplication, offer.monthlyMinimumApplication]
  );

  const accountHolderForm = useForm({
    resolver: zodResolver(accountHolderSchema),
    mode: 'all',
    reValidateMode: 'onSubmit',
    defaultValues: {
      pensionAge: orderState.pensionAge,
      taxSystem: orderState.taxSystem,
      paymentMethod: orderState.paymentMethod,
      dueDate: orderState.dueDate,
      bankAccountId: orderState.bankAccountId,
      singleContribution: Utils.Mask.Money(orderState.singleContribution),
      monthlyContribution: Utils.Mask.Money(orderState.monthlyContribution),
      beneficiariesPercentage: currentParticipationPercentage,
    },
  });

  const {
    setValue: setFormValue,
    watch,
    formState: { isValid },
  } = accountHolderForm;

  const addBeneficiary = useCallback(
    (beneficiary: PrivatePensionBeneficiary) => {
      setOrderState((prev) => ({ ...prev, beneficiaries: [...prev.beneficiaries, beneficiary] }));
    },
    [setOrderState]
  );

  const updateBeneficiary = useCallback(
    (index: number, beneficiary: PrivatePensionBeneficiary) => {
      setOrderState((prev) => {
        const newBeneficiaries = [...prev.beneficiaries];
        newBeneficiaries[index] = beneficiary;
        return { ...prev, beneficiaries: newBeneficiaries };
      });
    },
    [setOrderState]
  );

  const removeBeneficiary = useCallback(
    (index: number) => {
      setOrderState((prev) => ({
        ...prev,
        beneficiaries: prev.beneficiaries.filter((_, i) => i !== index),
      }));
    },
    [setOrderState]
  );

  const addPortability = useCallback(
    (newPortabilityInfo: PrivatePensionPortabilityInfo) => {
      setOrderState((prev) => ({
        ...prev,
        portabilities: [...prev.portabilities, newPortabilityInfo],
      }));
    },
    [setOrderState]
  );

  const updatePortability = useCallback(
    (index: number, portabilityInfo: PrivatePensionPortabilityInfo) => {
      setOrderState((prev) => {
        const newPortabilities = [...prev.portabilities];
        newPortabilities[index] = portabilityInfo;
        return { ...prev, portabilities: newPortabilities };
      });
    },
    [setOrderState]
  );

  const removePortability = useCallback(
    (index: number) => {
      setOrderState((prev) => ({
        ...prev,
        portabilities: prev.portabilities.filter((_, i) => i !== index),
      }));
    },
    [setOrderState]
  );

  const setKind = useCallback(
    (kind: PrivatePensionKind) => {
      setOrderState((prev) => ({ ...prev, kind }));
    },
    [setOrderState]
  );

  const setAccountHolder = useCallback(
    (accountHolder: PrivatePensionAccountHolder | null) => {
      setOrderState((prev) => ({
        ...prev,
        accountHolder:
          accountHolder !== null
            ? {
                ...accountHolder,
                manuallySet: true,
              }
            : null,
      }));
    },
    [setOrderState]
  );

  const setPensionAge = useCallback(
    (pensionAge: number) => {
      setOrderState((prev) => ({ ...prev, pensionAge }));
      setFormValue('pensionAge', pensionAge);
    },
    [setFormValue, setOrderState]
  );

  const setTaxSystem = useCallback(
    (taxSystem: PrivatePensionTaxSystem) => {
      setOrderState((prev) => ({ ...prev, taxSystem }));
      setFormValue('taxSystem', taxSystem);
    },
    [setFormValue, setOrderState]
  );

  const setSingleContribution = useCallback(
    (singleContribution: number) => {
      setOrderState((prev) => ({ ...prev, singleContribution }));
      setFormValue('singleContribution', Utils.Mask.Money(singleContribution));
    },
    [setFormValue, setOrderState]
  );

  const setMonthlyContribution = useCallback(
    (monthlyContribution: number) => {
      setOrderState((prev) => ({ ...prev, monthlyContribution }));
      setFormValue('monthlyContribution', Utils.Mask.Money(monthlyContribution));
    },
    [setFormValue, setOrderState]
  );

  const setPaymentMethod = useCallback(
    (paymentMethod: PrivatePensionPaymentMethod) => {
      setOrderState((prev) => ({ ...prev, paymentMethod, dueDate: undefined }));
      setFormValue('paymentMethod', paymentMethod);
      setFormValue('dueDate', undefined as unknown as number);
    },
    [setFormValue, setOrderState]
  );

  const setDueDate = useCallback(
    (dueDate: number) => {
      setOrderState((prev) => ({ ...prev, dueDate }));
      setFormValue('dueDate', dueDate);
    },
    [setFormValue, setOrderState]
  );

  const setBankAccountId = useCallback(
    (bankAccountId: number) => {
      setOrderState((prev) => ({ ...prev, bankAccountId, dueDate: undefined }));
      setFormValue('bankAccountId', bankAccountId);
      setFormValue('dueDate', undefined as unknown as number);
    },
    [setFormValue, setOrderState]
  );

  const addManualBankAccount = useCallback(
    (bankAccount: PrivatePensionClientBankAccountEntityNormalized) => {
      setOrderState((prev) => ({
        ...prev,
        bankAccounts: [
          ...prev.bankAccounts,
          { ...bankAccount, manuallyAdded: true, bankAccountId: 0 },
        ],
      }));
    },
    [setOrderState]
  );

  const removeManualBankAccount = useCallback(
    (index: number) => {
      setOrderState((prev) => ({
        ...prev,
        bankAccounts: prev.bankAccounts.filter((_, i) => i !== index),
      }));
    },
    [setOrderState]
  );

  const getCurrentBankAccount = useCallback(() => {
    const bankAccountId = orderState.bankAccountId;
    if (bankAccountId === undefined) {
      return undefined;
    }

    return (
      bankAccountsData?.data?.find((account) => account.bankAccountId === bankAccountId) ??
      orderState.bankAccounts.find((account) => account.bankAccountId === bankAccountId)
    );
  }, [bankAccountsData?.data, orderState.bankAccountId, orderState.bankAccounts]);

  const generateOrderData = (
    observation: string,
    sendingChannel: string,
    customChannelName?: string
  ): SolicitarAplicacaoPrevidencia => {
    const accountHolderData: ResponsavelDTO[] = [];

    if (orderState.accountHolder?.manuallySet) {
      accountHolderData.push({
        idTipoResponsavel: 2,
        idTipoDocumentoCivil: 7,
        tipoDocumentoCivil: 'CPF',
        cpfResponsavel: orderState.accountHolder.cpf,
        dataNascimento: orderState.accountHolder.birthDate,
        email: orderState.accountHolder.email,
        nome: orderState.accountHolder.name,
        numeroDocumentoIdentificacao: orderState.accountHolder.cpf,
        idParentesco: orderState.accountHolder?.relatedPartyId,
      });
    } else {
      accountHolderData.push({
        idTipoResponsavel: 1,
        idTipoDocumentoCivil: 7,
        tipoDocumentoCivil: 'CPF',
        cpfResponsavel: customerData?.personalInformation.cpfCnpjNormalized || '',
        dataNascimento: customerData?.personalInformation.birthDate as unknown as Date,
        email: customerData?.personalInformation.email || '',
        nome: customerData?.personalInformation.fullNameNormalized || '',
        numeroDocumentoIdentificacao: customerData?.personalInformation.cpfCnpjNormalized || '',
      });
    }

    const beneficiaryData: BeneficiarioDTO[] = orderState.beneficiaries.map((beneficiary) => ({
      idParentesco: beneficiary.relatedPartyId,
      dataNascimento: beneficiary.birthDate,
      nomeBeneficiario: beneficiary.name,
      porcentagemParticipacao: beneficiary.participationPercentage,
    }));

    const portabilityData: PortabilidadeDTO[] = orderState.portabilities.map((portability) => {
      return {
        idSeguradora: portability.clientInsuranceCompany,
        cnpjSeguradora: portability.clientInsuranceCompanyDocument,
        cnpjFundo: portability.fundDocument,
        numeroProcessoSusep: portability.susepNumber,
        tipoProduto: 1,
        idRegimeTributacao: portability.taxSystem,
        nomePlano: portability.planName,
        numeroCertificadoApolice: portability.certificateNumber,
        idTipoEntidade: 1,
        tipoPortabilidade: portability.transferType,
        valorPortabilidade: portability.value,
        fluxoPortabilidade: 1,
      };
    });

    const isBankSlip = orderState.paymentMethod === PrivatePensionPaymentMethod.BankSlip;
    const bankAccountData = isBankSlip ? undefined : getCurrentBankAccount();

    const baseParams = {
      cliente: {
        idEsteveEUA: undefined,
        cpf: customerData?.personalInformation.cpfCnpjNormalized || '',
        origemRecurso: undefined,
        idConta: accountId,
        idPaisOutraNacionalidade: undefined,
        nif: undefined,
        responsavel: accountHolderData,
      },
      documentoPrevidencia: {
        listBeneficiario: beneficiaryData,
        cobranca: {
          idBanco:
            bankAccountData?.bankAccountId === 0 ? undefined : bankAccountData?.bankAccountId,
          codigoBanco: bankAccountData?.bankCode,
          idDiaVencimento: orderState.dueDate,
          idFormaPagamento: orderState.paymentMethod,
          numeroContaCorrente: bankAccountData?.accountNumber,
          digitoVerificador: bankAccountData?.accountDigit,
          numeroAgencia: bankAccountData?.agencyNumber,
          digitoVerificadorAgencia: bankAccountData?.agencyDigit,
        },
        portabilidade: portabilityData,
        cnpjFundo: offer.productDocument,
        idRegimeTributario: orderState.taxSystem,
        idadeConcessao: orderState.pensionAge,
        valorAporteInicial: orderState.singleContribution,
        valorContribuicao: orderState.monthlyContribution,
        tipoProduto: orderState.kind,
      },
      canalRelacionamento: 3,
      ipCliente: '',
      pontoDeVendaId: undefined,
      propostasBaseDeDados: undefined,

      // idFundo: offer.privatePensionId,
      // idConta: accountId,
      // valor: orderState.singleContribution,

      tipoConfirmacaoOrdem: sendingChannel,
      observacao: observation,
      customChannel: legacySendingChannelIsOther ? customChannelName : '',
    };

    // NOTE: This is done because there are some fields above that are not present in the type
    //       but are sent in the legacy code
    return baseParams as unknown as SolicitarAplicacaoPrevidencia;
  };

  const findPortabilityDataFromCertificateNumber = async (certificateNumber: string) => {
    try {
      const result =
        (await Service.External.Extranet.Previdencia.V1.previdenciaObterCertificadosAptosPortabilidade(
          {
            idCertificado: Number(certificateNumber),
            idConta: accountId,
            /** @ts-expect-error Need to pass null */
            idSeguradora: 'null',
            screenName: 'PrivatePensionPortabilityModal',
          }
        )) as ReturnTypeOf_PrevidenciaObterCertificadosAptosPortabilidade;

      if (!result.length) {
        setFundOptions([]);

        return undefined;
      }

      const contractData = result[0];

      setFundOptions(
        contractData.fundos
          .map((fund) => {
            return {
              value: fund.cnpj,
              label: fund.nome.toString(),
            };
          })
          .filter((fund) => fund !== null)
      );

      let defaultFundDocument: string | undefined;

      if (contractData.fundos.length === 1) {
        defaultFundDocument = contractData.fundos[0].cnpj;
      }

      const returnData: Partial<PrivatePensionPortabilityInfo> = {
        susepNumber: contractData.susep,
        susepNumberAutofilled: true,
        value: contractData.valorBruto,
        clientInsuranceCompany: contractData.cedente.idSeguradora,
        clientInsuranceCompanyDocument: contractData.cedente.cnpj,
        fundDocument: defaultFundDocument,
        taxSystem:
          contractData.regimeTributario === 2
            ? PrivatePensionTaxSystem.Regressive
            : PrivatePensionTaxSystem.Progressive,
        taxSystemAutofilled: true,
        rentabilityType:
          contractData.situacaoRegime === 1
            ? PrivatePensionPortabilityRentabilityType.FixedReturnRate
            : PrivatePensionPortabilityRentabilityType.VariableReturnRate,
        rentabilityTypeAutofilled: true,
        certificateNumber,
        planName: contractData.descricao,
      };

      return returnData;
    } catch (_error) {
      return undefined;
    }
  };

  const findPortabilityDataFromSusepNumber = async (susepNumber: string) => {
    try {
      const { data } = await Service.Pension.V1.getConsultSusep({
        susepNumber,
        screenName: 'PrivatePensionPortabilityModal',
      });

      const returnData: Partial<PrivatePensionPortabilityInfo> = {};

      if (!data.funds?.length) {
        setFundOptions([]);

        return undefined;
      }

      setFundOptions(
        data.funds.map((fund) => {
          return {
            value: fund.cnpj?.toString() || '',
            label: fund.name?.toString() || '',
          };
        })
      );

      if (data.funds?.length === 1) {
        returnData.fundDocument = data.funds[0].cnpj || '';
      }

      if (data.insurerId && data.cnpjAssignor) {
        returnData.clientInsuranceCompanyDocument = data.cnpjAssignor;
        returnData.clientInsuranceCompany = insuranceData?.data.find(
          (insurance) => insurance.insuranceId === data.insurerId
        )?.insuranceId;
      }

      if (data.description) {
        returnData.planName = data.description;
      }

      return returnData;
    } catch (_error) {
      return undefined;
    }
  };

  useEffect(() => {
    setFormValue('beneficiariesPercentage', currentParticipationPercentage, {
      shouldValidate: true,
    });
  }, [currentParticipationPercentage, setFormValue]);

  useEffect(() => {
    if (relatedPartyData?.data?.length) {
      const options = relatedPartyData.data.map((party) => ({
        value: party.relatedPartyId.toString(),
        label: party.name,
      }));

      setRelatedPartyOptions(options);
    }
  }, [relatedPartyData]);

  useEffect(() => {
    if (insuranceData?.data?.length) {
      const options = insuranceData.data.map((insurance) => ({
        value: insurance.insuranceId.toString(),
        label: insurance.insuranceName,
      }));

      setInsuranceOptions(options);
    }
  }, [insuranceData]);

  useEffect(() => {
    if (insuranceCompanyParametersData?.banks?.length) {
      const options = insuranceCompanyParametersData.banks.map((bank) => ({
        value: bank.bankId.toString(),
        label: bank.name,
      }));

      setBankOptions(options);
    }
  }, [insuranceCompanyParametersData]);

  useEffect(() => {
    if (bankAccountsData?.data?.length) {
      setBankAccounts(bankAccountsData.data);
    }
  }, [bankAccountsData]);

  useEffect(() => {
    if (customerData) {
      setOrderState((prev) => ({
        ...prev,
        accountHolder: {
          ...(prev.accountHolder ? prev.accountHolder : ({} as PrivatePensionAccountHolder)),
          name: customerData.personalInformation.fullNameNormalized,
          email: customerData.personalInformation.emailNormalized,
          cpf: customerData.personalInformation.cpfCnpjNormalized?.toString() || '',
          birthDate: customerData.personalInformation.birthDate || (undefined as unknown as Date),
        },
      }));
    }
  }, [customerData, setOrderState]);

  const singleContributionForm = watch('singleContribution');
  const monthlyContributionForm = watch('monthlyContribution');

  useEffect(() => {
    setSingleContribution(Utils.Unmask.Money(singleContributionForm));
  }, [setSingleContribution, singleContributionForm]);

  useEffect(() => {
    setMonthlyContribution(Utils.Unmask.Money(monthlyContributionForm));
  }, [monthlyContributionForm, setMonthlyContribution]);

  return {
    addBeneficiary,
    updateBeneficiary,
    removeBeneficiary,
    beneficiaries: orderState.beneficiaries,
    isValid,
    kind: orderState.kind,
    setKind,
    hasPortability,
    hasBeneficiary,
    portabilities: orderState.portabilities,
    addPortability,
    updatePortability,
    removePortability,
    accountHolder: orderState.accountHolder,
    setAccountHolder,
    pensionAge: orderState.pensionAge,
    setPensionAge,
    taxSystem: orderState.taxSystem,
    setTaxSystem,
    singleContribution: orderState.singleContribution,
    setSingleContribution,
    monthlyContribution: orderState.monthlyContribution,
    setMonthlyContribution,
    paymentMethodOptions,
    paymentMethod: orderState.paymentMethod,
    setPaymentMethod,
    dueDateOptions,
    isDueDateEnabled,
    dueDate: orderState.dueDate,
    setDueDate,
    bankAccountId: orderState.bankAccountId,
    setBankAccountId,
    taxSystemOptions,
    bankOptions,
    bankConfigurations: insuranceCompanyParametersData?.bankValidationSettings,
    bankAccounts,
    bankAccountsLoading,
    bankAccountOptions,
    addManualBankAccount,
    removeManualBankAccount,
    getCurrentBankAccount,
    relatedPartyOptions,
    relatedPartyLoading,
    fundOptions,
    insuranceOptions,
    insuranceLoading,
    currentParticipationPercentage,
    maxParticipationPercentage,
    portabilityAmount,
    accountHolderForm,
    customerData,
    customerLoading,
    customerError,
    customerRefresher,
    hasLegacySendingChannel,
    legacySendingChannelIsOther,
    legacySendingChannel,
    legacySendingChannelsOptions,
    setLegacySendingChannel,
    generateOrderData,
    insuranceCompanyParametersLoading,
    findPortabilityDataFromCertificateNumber,
    findPortabilityDataFromSusepNumber,
  };
};

export type PrivatePensionAssetController = ReturnType<typeof usePrivatePensionAssetController>;
